import React from 'react';
import { Button, Dropdown, Modal, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { usePermissions } from '../../hooks/usePermissions';

/** A custom row action rendered as a menu item in the "⋯" dropdown. */
export interface RowMenuAction {
    key: string;
    label: React.ReactNode;
    icon?: React.ReactNode;
    onClick: () => void;
    danger?: boolean;
    disabled?: boolean;
    /** Convenience: when true the action is omitted entirely. */
    hidden?: boolean;
    /** Tooltip shown on hover (useful for disabled items). */
    tooltip?: string;
    /** When set, confirm via Modal before running onClick. */
    confirm?: { title: string; description?: React.ReactNode; okText?: string };
}

interface RowActionsProps {
    onView?: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
    /** Confirmation copy for the delete action. */
    deleteTitle?: string;
    deleteDescription?: React.ReactNode;
    viewLabel?: string;
    editLabel?: string;
    deleteLabel?: string;
    /** Gates the built-in edit/delete items via dynamic permissions. */
    permissionModule?: string;
    /** Custom actions inserted between edit and delete. */
    items?: RowMenuAction[];
    /** Disable the whole menu. */
    disabled?: boolean;
}

/**
 * Uniform row actions: a single "⋯" button that opens a dropdown menu with all
 * actions. Every table's Actions column looks identical (one icon) regardless of
 * how many actions a row has. Built-in View / Edit / Delete plus any custom
 * `items`; delete (and any item with `confirm`) prompts a Modal first.
 */
const RowActions: React.FC<RowActionsProps> = ({
    onView,
    onEdit,
    onDelete,
    deleteTitle = 'Delete this item?',
    deleteDescription,
    viewLabel = 'View',
    editLabel = 'Edit',
    deleteLabel = 'Delete',
    permissionModule,
    items = [],
    disabled = false,
}) => {
    const { hasPermission } = usePermissions();
    const canEdit = !permissionModule || hasPermission(permissionModule, 'edit');
    const canDelete = !permissionModule || hasPermission(permissionModule, 'delete');

    const registry: Record<string, RowMenuAction> = {};
    const menuItems: MenuProps['items'] = [];

    if (onView) {
        registry.__view = { key: '__view', label: viewLabel, icon: <EyeOutlined />, onClick: onView };
        menuItems.push({ key: '__view', label: viewLabel, icon: <EyeOutlined /> });
    }
    if (onEdit) {
        registry.__edit = { key: '__edit', label: editLabel, icon: <EditOutlined />, onClick: onEdit, disabled: !canEdit };
        menuItems.push({ key: '__edit', label: editLabel, icon: <EditOutlined />, disabled: !canEdit });
    }
    items.filter(a => !a.hidden).forEach(a => {
        registry[a.key] = a;
        const label = a.tooltip ? <Tooltip title={a.tooltip}><span>{a.label}</span></Tooltip> : a.label;
        menuItems.push({ key: a.key, label, icon: a.icon, danger: a.danger, disabled: a.disabled });
    });
    if (onDelete) {
        if (menuItems.length) menuItems.push({ type: 'divider' });
        registry.__delete = {
            key: '__delete',
            label: deleteLabel,
            icon: <DeleteOutlined />,
            onClick: onDelete,
            danger: true,
            disabled: !canDelete,
            confirm: { title: deleteTitle, description: deleteDescription, okText: 'Delete' },
        };
        menuItems.push({ key: '__delete', label: deleteLabel, icon: <DeleteOutlined />, danger: true, disabled: !canDelete });
    }

    const handleClick: MenuProps['onClick'] = ({ key, domEvent }) => {
        domEvent.stopPropagation();
        const action = registry[key];
        if (!action || action.disabled) return;
        if (action.confirm) {
            Modal.confirm({
                title: action.confirm.title,
                content: action.confirm.description,
                okText: action.confirm.okText ?? 'Confirm',
                okType: action.danger ? 'danger' : 'primary',
                cancelText: 'Cancel',
                onOk: action.onClick,
            });
        } else {
            action.onClick();
        }
    };

    if (!menuItems.length) return null;

    return (
        <Dropdown menu={{ items: menuItems, onClick: handleClick }} trigger={['click']} placement="bottomRight" disabled={disabled}>
            <Button type="text" size="small" icon={<MoreOutlined />} onClick={e => e.stopPropagation()} />
        </Dropdown>
    );
};

export default RowActions;
